import React, { useEffect, useState } from "react";
import { defaultAction } from "@actions/defaultAction";
import { useDispatch, useSelector } from "react-redux";
import { initAppBrowser } from "@actions/initApp";
import { SHOW_SELL_RECEIPT_MODAL } from "@constants/actionTypes";
import SellOrderCompleteWrapper from "@components/SellOrderCompleteWrapper";
import SellCompleteHeader from "@components/SellCompleteHeader";
import styled from "@emotion/styled";
import Instructions from "@components/Instructions";
import DefaultCustomButton from "@modules/Components/DefaultCustomButton";
import { ClearedStyleRouterLink, ClearedStyleHref } from "@modules/Components";
import CardCashSpinner from "@components/CardCashSpinner";
import { useHistory } from "react-router-dom";

const Href = styled(ClearedStyleHref)`
  white-space: nowrap;
  display: inline-block;
`;

const MainContent = styled.div`
  margin: 0 auto;
  max-width: 935px;
`;

const InnerContainerImageGradient = styled.div`
  background-image: linear-gradient(
    to bottom,
    rgba(34, 176, 252, 0.071),
    rgba(34, 176, 252, 0.01)
  );
  background-repeat: no-repeat;
  mix-blend-mode: luminosity;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  opacity: 0.7;
  pointer-events: none;
  height: 100%;
`;

const Bottom = styled.div`
  font-size: 16px;
  line-height: 1.88;
  color: #777984;
  margin-top: 60px;
`;

const ButtonContainer = styled.div`
  width: 203px;
  margin-top: 45px;
  margin-bottom: 110px;
`;

const BottomP = styled.div`
  margin-bottom: 7px;
`;

const BottomSpan = styled.span`
  color: #ff9c1b;
  cursor: pointer;
`;

const BottomBoldSpan = styled.span`
  font-weight: 500;
  color: #1f2021;
`;

const LoaderContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: white;
  background-image: linear-gradient(
    to bottom,
    rgba(34, 176, 252, 0.04),
    rgba(34, 176, 252, 0)
  );
`;

const Loader = styled.div`
  margin-top: 250px;
`;

const SellOrderComplete = () => {
  const dispatch = useDispatch();
  const checkoutOrdersSell = useSelector(state => state.checkoutOrdersSell);
  const supportEmail = useSelector(state => state.config.supportEmail);
  const supportPhone = useSelector(state => state.config.supportPhone);
  const history = useHistory();

  const [gettingOrder, setGettingOrder] = useState(false);
  const [resourceID, setResourceID] = useState(false);
  const [marketingSent, setMarketingSent] = useState(false);

  useEffect(() => {
    dispatch(initAppBrowser(["messages", "sell"]));

    getOrderResourceToShow();
  }, []);

  useEffect(() => {
    if (checkoutOrdersSell[resourceID]?.cardsValue && !marketingSent) {
      const uetq = window.uetq || [];
      uetq.push("event", "", {
        revenue_value: checkoutOrdersSell[resourceID].cardsValue,
        currency: "USD"
      });
      setMarketingSent(true);
    }
  }, [checkoutOrdersSell[resourceID]]);

  useEffect(() => {
    if (gettingOrder && !resourceID) {
      history.push("/order-status/");
    }
  }, [gettingOrder]);

  const getOrderResourceToShow = () => {
    let newestPlacedDate = 0;
    let refIdOfNewestOrderPlaced = null;

    for (let key of Object.keys(checkoutOrdersSell)) {
      if (checkoutOrdersSell[key]._thunkRunning) {
        return false;
      }

      if (checkoutOrdersSell[key].date && checkoutOrdersSell[key].date.placed) {
        let orderPlacedDate = new Date(
          checkoutOrdersSell[key].date.placed
        ).valueOf();
        if (orderPlacedDate > newestPlacedDate) {
          newestPlacedDate = orderPlacedDate;
          refIdOfNewestOrderPlaced = key;
        }
      }
    }
    setGettingOrder(true);
    setResourceID(refIdOfNewestOrderPlaced);
  };

  let sellOrderToRender = checkoutOrdersSell[resourceID];

  if (!resourceID) {
    return (
      <LoaderContainer>
        <Loader>
          <CardCashSpinner />
        </Loader>
      </LoaderContainer>
    );
  }

  return (
    <div>
      {sellOrderToRender &&
        checkoutOrdersSell &&
        checkoutOrdersSell[resourceID] && (
          <div>
            <InnerContainerImageGradient />
            {sellOrderToRender?.affiliate &&
              sellOrderToRender.affiliate !== 1006 && (
                <img
                  src={`https://shareasale.com/sale.cfm?amount=${sellOrderToRender.cardsValue}&tracking=${sellOrderToRender.orderId}&transtype=sale&merchantID=55161&xtype=SellPage`}
                  width="1"
                  height="1"
                />
              )}

            <SellOrderCompleteWrapper checkoutOrdersSellID={resourceID}>
              <SellCompleteHeader checkoutOrdersSellID={resourceID} />
              <MainContent className="main-container-page-width">
                <Instructions
                  type={checkoutOrdersSell[resourceID].type}
                  checkoutOrdersSellID={resourceID}
                  sellOrderToRender={sellOrderToRender}
                />
                <Bottom>
                  <BottomP>
                    Thank you for choosing CardCash. Details about your order
                    have been sent to and you can always check the status of
                    your order
                    <ClearedStyleRouterLink to="/order-status/">
                      <BottomSpan>&nbsp;here&nbsp;</BottomSpan>
                    </ClearedStyleRouterLink>
                    or by looking at your
                    <ClearedStyleRouterLink to="/account/sell/">
                      <BottomSpan>&nbsp;account history</BottomSpan>
                    </ClearedStyleRouterLink>
                    .
                  </BottomP>
                  <BottomP>
                    If you have any questions please contact us via
                    <Href href={`mailto:${supportEmail}`}>
                      <BottomSpan>&nbsp;{supportEmail}&nbsp;</BottomSpan>
                    </Href>
                    or
                    <Href href={`tel:${supportPhone}`}>
                      <BottomSpan>&nbsp;{supportPhone}</BottomSpan>
                    </Href>
                    .
                  </BottomP>
                  <BottomP>
                    {checkoutOrdersSell[resourceID].type !== "ecode" ? (
                      <>
                        <BottomBoldSpan>
                          Please note, if the verified balance of the sold cards
                          is different than the values you reported, then the
                          payout amount will be adjusted accordingly. Failure to
                          send the cards to the proper address may result in the
                          loss of your package
                        </BottomBoldSpan>
                        . You can mail your cards to us via regular mail. If you
                        are sending more than one card, we recommend using a
                        thick envelope. As an alternative, you can tape the
                        cards into place or tape the side of the envelope. If
                        you are sending high value cards, we recommend that you
                        add delivery confirmation to your package for your
                        protection.
                      </>
                    ) : (
                      <>
                        Please note, if the verified balance of the sold cards
                        is different than the values you reported, then the
                        payout amount will be adjusted accordingly.
                      </>
                    )}
                  </BottomP>
                </Bottom>
                <ButtonContainer>
                  <DefaultCustomButton
                    handleClick={() =>
                      dispatch(
                        defaultAction(SHOW_SELL_RECEIPT_MODAL, resourceID)
                      )
                    }
                    classes={"main-default-orange-button"}
                    type="button"
                    disabled={false}
                    buttonType={"WHITE_BUTTON_ORANGE_COLOR"}
                  >
                    <span>View Receipt</span>
                  </DefaultCustomButton>
                </ButtonContainer>
              </MainContent>
            </SellOrderCompleteWrapper>
          </div>
        )}
    </div>
  );
};

export default SellOrderComplete;
